<script>
import { AbcBadge } from "@ordbogen-npm/abc-dsk";
import { VueDiff } from "@ordbogen-npm/vue-diff";
import { mapStores } from "pinia";
import { defineComponent } from "vue";

import { isDownArrow, isEnter, isEscape, isUpArrow } from "@/common/event-helpers";
import IconEnter from "@/components/icons/white/IconEnter.vue";
import { useSearchStore } from "@/stores";

export default defineComponent({
  name: "WordCompletion",
  components: {
    IconEnter,
    VueDiff,
    AbcBadge
  },
  props: {
    searchInputFocus: {
      type: Boolean,
      default: false
    }
  },
  emits: ["selectResult"],
  data() {
    return {
      activeIndex: -1,
      // dismissed must default to false.
      // Otherwise the very first search won't open word completion
      // automatically on the front page.
      dismissed: false,
      wasActiveBeforeBlur: false,
      windowHasFocus: true
    };
  },
  computed: {
    ...mapStores(useSearchStore),

    searchWord() {
      return this.searchStore.searchWord;
    },
    wordCompletionList() {
      return this.searchStore.wordCompletionList;
    },

    firstSmartCompleteEntryIndex() {
      return this.wordCompletionList?.findIndex((wordCompletionEntry) =>
        this.isSmartOrPhraseCompletion(wordCompletionEntry)
      );
    },
    hasPrefixEntries() {
      return this.wordCompletionList?.some((wordCompletionEntry) =>
        this.isPrefixCompletion(wordCompletionEntry)
      );
    },
    isActive() {
      // Override to prevent weird behavior when clicking the window with another window active (non-macOS)
      if (!this.windowHasFocus && this.wasActiveBeforeBlur && this.wordCompletionList.length) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.searchStore.isWordCompletionOpen = true;
        return true;
      }

      let isActive = true;

      if (!this.wordCompletionList.length) {
        isActive = false;
      }

      if (this.dismissed || !this.searchInputFocus) {
        isActive = false;
      }

      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.searchStore.isWordCompletionOpen = isActive;
      return isActive;
    }
  },
  watch: {
    isActive(isActive) {
      // Handle window blur focus override
      if (isActive) {
        this.wasActiveBeforeBlur = true;
        return;
      }

      // This setTimeout prevents an edge case where wasActiveBeforeWindowBlur
      // would be set to false right before the window.onblur event ran.
      // This results in the wordcompletion box being
      // hidden (but still clickable) when blurring the window.
      setTimeout(() => {
        if (!this.searchInputFocus && this.windowHasFocus) {
          this.wasActiveBeforeBlur = false;
        }
      }, 5);
    },
    async searchInputFocus(hasFocus) {
      if (!hasFocus) {
        return this.close();
      }

      // Ensure we get a fresh word completion list
      await this.searchStore.wordCompletion(this.searchWord);

      if (this.wordCompletionList?.length) {
        return this.open();
      }
    },
    wordCompletionList: {
      deep: true,
      handler(list) {
        if (list.length) {
          this.open();
        }
      }
    }
  },
  mounted() {
    this.addGlobalEventListeners();
  },
  beforeUnmount() {
    this.removeGlobalEventListeners();
  },
  methods: {
    isPrefixCompletion(wordCompletionEntry) {
      return wordCompletionEntry.type === "prefix";
    },
    isSmartCompletion(wordCompletionEntry) {
      return wordCompletionEntry.type === "smart";
    },
    isPhraseCompletion(wordCompletionEntry) {
      return wordCompletionEntry.type === "phrase";
    },
    isSmartOrPhraseCompletion(wordCompletionEntry) {
      return (
        this.isSmartCompletion(wordCompletionEntry) || this.isPhraseCompletion(wordCompletionEntry)
      );
    },
    addGlobalEventListeners() {
      window.addEventListener("keydown", this.handleKeydownEvent);
      window.addEventListener("focus", this.handleWindowFocus);
      window.addEventListener("blur", this.handleWindowBlur);
    },
    removeGlobalEventListeners() {
      window.removeEventListener("keydown", this.handleKeydownEvent);
      window.removeEventListener("focus", this.handleWindowFocus);
      window.removeEventListener("blur", this.handleWindowBlur);
    },
    open() {
      this.dismissed = false;
      this.activeIndex = -1;
    },
    close() {
      this.dismissed = true;
      this.activeIndex = -1;
    },
    search() {
      const entry = this.wordCompletionList[this.activeIndex];
      if (!entry) {
        return;
      }

      const { uuid, type: engine, word } = entry;

      this.searchStore.searchWord = word;
      this.$emit("selectResult");
      this.searchStore.logWordCompletionSelection({
        uuid,
        engine,
        index: this.activeIndex,
        selectedWord: word
      });
      this.close();
    },
    handleWindowFocus() {
      this.windowHasFocus = true;
    },
    handleWindowBlur() {
      this.windowHasFocus = false;
    },
    handleKeydownEvent(event) {
      switch (true) {
        case isUpArrow(event):
          event.preventDefault();
          this.activeIndex--;

          // Loop when we can't go further up the list
          if (this.activeIndex < 0) {
            this.activeIndex = this.wordCompletionList.length - 1;
          }

          break;

        case isDownArrow(event):
          event.preventDefault();

          if (!this.isActive) {
            this.activeIndex = 0;
            this.open();
            return;
          }

          this.activeIndex++;

          // Loop when we can't go further down the list
          if (this.activeIndex >= this.wordCompletionList.length) {
            this.activeIndex = 0;
          }

          break;

        case isEscape(event) && this.isActive:
          // We don't want to clear the search field if word completion is open
          event.preventDefault();
          this.wasActiveBeforeBlur = false;
          this.close();
          break;

        case isEnter(event):
          // We don't want to hijack a regular search
          if (this.activeIndex === -1) {
            return;
          }

          event.preventDefault();
          this.search();

          // Replicate behavior when searching without wordcompletion
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement?.blur();
          }
          break;
      }
    }
  }
});
</script>

<template>
  <div v-if="isActive" class="word-completion-container">
    <div class="word-completion" @mouseleave="activeIndex = -1">
      <div
        v-for="(wordCompletionEntry, wordCompletionIndex) in wordCompletionList"
        :key="wordCompletionIndex"
      >
        <div
          v-if="wordCompletionIndex === firstSmartCompleteEntryIndex && hasPrefixEntries"
          class="word-completion__entry-spacer"
        ></div>
        <div
          v-if="wordCompletionIndex === firstSmartCompleteEntryIndex"
          class="word-completion__entry word-completion__entry--did-you-mean"
        >
          {{ $t("searchBar.wordCompletion.smartCompleteHeader") }}
        </div>
        <div
          class="word-completion__entry"
          :class="{
            'word-completion__entry--selected': activeIndex === wordCompletionIndex,
            'word-completion__entry--word-completion': isPrefixCompletion(wordCompletionEntry),
            'word-completion__entry--smart-completion':
              isSmartOrPhraseCompletion(wordCompletionEntry)
          }"
          dir="auto"
          @mousedown="search()"
          @mouseenter="activeIndex = wordCompletionIndex"
        >
          <AbcBadge
            v-if="isSmartOrPhraseCompletion(wordCompletionEntry)"
            class="word-completion__entry-type"
            color="secondary"
          >
            {{ $t("searchBar.wordCompletion.smartCompleteBadgeLabel") }}
          </AbcBadge>
          <VueDiff
            v-if="isSmartCompletion(wordCompletionEntry)"
            :text="searchWord"
            :new-text="wordCompletionEntry.word"
            ignore-case
            class="word-completion__entry-word"
          >
            <template #insert="{ value }">
              <ins class="word-completion__highlight word-completion__highlight--insert">{{
                value
              }}</ins>
            </template>
            <template #replace="{ value }">
              <ins class="word-completion__highlight word-completion__highlight--insert">{{
                value
              }}</ins>
            </template>
            <template #equal="{ value }">{{ value }}</template>
          </VueDiff>
          <span v-else class="word-completion__entry-word">
            {{ wordCompletionEntry.word }}
          </span>
          <span
            v-if="activeIndex === wordCompletionIndex"
            class="word-completion__entry-enter-hint"
          >
            <span class="mx-2">{{ $t("searchBar.wordCompletion.selectHint") }}</span>
            <IconEnter />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
